// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, tap } from 'rxjs/operators';

// Core packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { InterventionsService } from 'src/app/modules/interventions/interventions.service';
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { ICompany } from 'src/app/shared/models/company/company.interface';
import { PropertyInterface } from 'src/app/shared/models/property/property.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { InterventionsDataSource } from 'src/app/shared/utils/data-sources/interventions-data-source';

@Component({
  selector: 'app-interventions-list',
  templateUrl: './interventions-list.page.html',
  styleUrls: ['./interventions-list.page.scss']
})
export class InterventionsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  
  // BEGIN - Table interventions
  displayedColumns: string[] = ['index', 'name', 'propertyName', 'type', 'meetingResolutionDate', 'status', 'constructionStart', 'actions'];
  dataSource: InterventionsDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  nameFilter: FormControl = new FormControl('');
  propertyNameFilter: FormControl = new FormControl('');
  typeFilter: FormControl = new FormControl('');
  meetingResolutionDateFilter: FormControl = new FormControl('');
  statusFilter: FormControl = new FormControl('');
  constructionStartFilter: FormControl = new FormControl('');
  // END - Table interventions

  activeRoute: string;
  propertyIsLoading = false;
  availableProperties: PropertyInterface[] = [];
  companyIsLoading = false;
  availableCompanies: ICompany[] = [];
  availableStatus = [];

  /**
   * Class constructor
   */
  constructor(
    private interventionsService: InterventionsService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private propertiesService: PropertiesService,
    private companiesService: CompaniesService,
    private configService: ConfigService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('interventions.list.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Init data source
    this.dataSource = new InterventionsDataSource(this.interventionsService, this.helperService);

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('interventions');

    // Get available intervention status from configService
    this.availableStatus = this.configService.availableInterventionStatus;
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.propertyNameFilter.valueChanges.pipe(startWith(null as string)),
        this.typeFilter.valueChanges.pipe(startWith(null as string)),
        this.statusFilter.valueChanges.pipe(startWith(null as string)),
        this.meetingResolutionDateFilter.valueChanges.pipe(startWith(null as string)),
        this.constructionStartFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Load page only if at least one value is not null
          if (res && !res.every(value => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all interventions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'interventions',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        name: this.nameFilter.value,
        propertyName: this.propertyNameFilter.value,
        type: this.typeFilter.value,
        status: this.statusFilter.value,
        meetingResolutionDate: this.meetingResolutionDateFilter.value,
        constructionStart: this.constructionStartFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      name: this.nameFilter.value,
      propertyName: this.propertyNameFilter.value,
      type: this.typeFilter.value,
      status: this.statusFilter.value,
      meetingResolutionDate: this.meetingResolutionDateFilter.value ? new Date(this.meetingResolutionDateFilter.value).toISOString() : undefined,
      constructionStart: this.constructionStartFilter.value ? new Date(this.constructionStartFilter.value).toISOString() : undefined,
    };
    this.dataSource.loadItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export interventions in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('interventions.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.interventionsService.getExport().subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('interventions', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.nameFilter.setValue(null);
    this.propertyNameFilter.setValue(null);
    this.typeFilter.setValue(null);
    this.statusFilter.setValue(null);
    this.meetingResolutionDateFilter.setValue(null);
    this.constructionStartFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "name";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.name) {
                  this.nameFilter.setValue(setting.search.name);
                }
                if (setting.search && setting.search.propertyName) {
                  this.propertyNameFilter.setValue(setting.search.propertyName);
                }
                if (setting.search && setting.search.type) {
                  this.typeFilter.setValue(setting.search.type);
                }
                if (setting.search && setting.search.status) {
                  this.statusFilter.setValue(setting.search.status);
                }
                if (setting.search && setting.search.meetingResolutionDate) {
                  this.meetingResolutionDateFilter.setValue(setting.search.meetingResolutionDate);
                }
                if (setting.search && setting.search.constructionStart) {
                  this.constructionStartFilter.setValue(setting.search.constructionStart);
                }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayFn(arg?: PropertyInterface): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }
}
